import {Textarea} from 'platform/components';
import {Heading, Link, Show, Text, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {Button} from '../../components/Button/Button';
import {CardOo} from '../../components/CardOo/CardOo';
import {RadioButtonGroup} from '../../components/RadioButtonGroup/RadioButtonGroup';
import {StarRatingInput} from '../../components/StarRatingInput/StarRatingInput';
import {translateWithFallback as t} from '../../utils/translateWithFallback';
import {useRatingCard} from './useRatingCard';

const MAX_COMMENT_LENGTH = 2000;
const LOW_RATING_THRESHOLD = 3;
const RECOMMENDATION_OPTIONS = Array.from({length: 11}, (_, value) => ({
  value,
  label: value.toString(),
}));

export function RatingCard() {
  const card = useRatingCard();

  return (
    <CardOo>
      {match(card.state)
        .with({type: 'form'}, () => (
          <VStack spacing={4} data-testid="ratingCard-form">
            <Heading size={4}>
              {t('rating.title', 'How satisfied are you with your purchase?')}
            </Heading>
            <StarRatingInput
              value={card.rating}
              onChange={card.setRating}
              data-testid="ratingCard"
            />
            <VStack spacing={2}>
              <Text size="small">
                {t(
                  'rating.recommendation.label',
                  'How likely are you to recommend us to a friend? (0–10)'
                )}
              </Text>
              <RadioButtonGroup
                appearance="compact"
                label={t(
                  'rating.recommendation.label',
                  'How likely are you to recommend us to a friend? (0–10)'
                )}
                value={card.recommendation}
                options={RECOMMENDATION_OPTIONS}
                onChange={card.setRecommendation}
              />
            </VStack>
            <Textarea
              label={t('rating.comment.label', 'Anything you would like to tell us? (optional)')}
              value={card.comment}
              onChange={card.setComment}
              maxLength={MAX_COMMENT_LENGTH}
              isCounterVisible
            />
            <Button
              variant="primary"
              title={t('rating.submit', 'Send rating')}
              isDisabled={isNil(card.rating)}
              isLoading={card.isLoading}
              onClick={card.submit}
              data-testid="ratingCard-submit"
            />
            <Show when={card.hasError}>
              <Text size="small" color="warningDark" data-testid="ratingCard-error">
                {t('rating.error', 'Your rating could not be sent. Please try again.')}
              </Text>
            </Show>
          </VStack>
        ))
        .with({type: 'submitted'}, (answer) => (
          <VStack spacing={3} data-testid="ratingCard-result">
            <Heading size={4}>{t('rating.thanks', 'Thank you for your feedback.')}</Heading>
            <StarRatingInput value={answer.rating} isReadOnly />
            <Text>{answer.rating}/5</Text>
            <Show when={isNotNil(answer.recommendation)}>
              <Text>{answer.recommendation}/10</Text>
            </Show>
            <Show when={isNotNil(answer.comment)}>
              <Text size="small">{answer.comment}</Text>
            </Show>
            <Show when={answer.rating <= LOW_RATING_THRESHOLD}>
              <Text size="small" data-testid="ratingCard-lowRating">
                {t('rating.lowRating', 'We are sorry to hear that. Your seller will contact you.')}
              </Text>
            </Show>
            <Show when={card.owner}>
              {(owner) => (
                <Link
                  href={`mailto:${owner.email}`}
                  title={t('rating.contactSeller', 'Contact your seller')}
                />
              )}
            </Show>
          </VStack>
        ))
        .with({type: 'alreadyRated'}, () => (
          <VStack spacing={3} data-testid="ratingCard-result">
            <Heading size={4}>{t('rating.thanks', 'Thank you for your feedback.')}</Heading>
            <Text size="small">
              {t('rating.alreadyRated', 'You have already rated this purchase.')}
            </Text>
          </VStack>
        ))
        .exhaustive()}
    </CardOo>
  );
}
